import React from 'react';
import { LoaderCircleIcon, ArrowDownToLine, SendHorizontal } from 'lucide-react';

import { cn } from '@epoch-ui/lib/utils';
import { Button } from '@epoch-ui/components/Button';
import { QuestionResponses } from 'pages/dashboard/overview/events/editor/components/QuestionResponses/QuestionResponses';
import { CSV_OPTIONS } from 'utils/csv';
import dayjs from 'utils/extendedDayJS';
import { useCsvReportDownload } from 'hooks/useCsvReportDownload';
import { useToaster, ToasterVariant } from 'hooks/useToaster';

import { useEditableEvent } from '../../../hooks/useEditableEvent';
import { useResendFeedbackFormModal } from '../../../hooks/eventEditorState';
import { EditEventFeedbackData } from '../../../api/feedback/getEditEventFeedbackData';
import { EmptyFeedbackResponses } from '../components/EmptyFeedbackResponses';

interface FeedbackFormResponsesSectionProps {
  data: EditEventFeedbackData;
}

export const FeedbackFormResponsesSection = ({
  data
}: FeedbackFormResponsesSectionProps): React.ReactElement => {
  const { id, feedbackSchedule, datetimeEnd, postQuestions, scheduledDeletionAt } =
    useEditableEvent();
  const { feedbackSubmitted } = data.feedbackStats;
  const { showToast } = useToaster();
  const resendFeedbackFormModal = useResendFeedbackFormModal();

  const isCancelled = !!scheduledDeletionAt;
  const isThirtyDaysAfterEventEnds = dayjs.utc().isAfter(dayjs.utc(datetimeEnd).add(30, 'days'));

  const { loading: csvLoading, triggerCsvReportDownload } = useCsvReportDownload({
    type: CSV_OPTIONS.FEEDBACK,
    exportParams: id,
    onSuccess: (): void => {
      showToast('CSV downloaded');
    },
    onError: (errorMessage): void => {
      showToast(errorMessage, { variant: ToasterVariant.ERROR });
    }
  });

  return (
    <div>
      <div className="tw-flex tw-items-center tw-justify-between">
        <h2
          className={cn(
            'tw-m-0 tw-mb-4 tw-text-xl tw-font-medium tw-leading-7',
            isCancelled && 'tw-opacity-40'
          )}
        >
          Feedback form responses
        </h2>

        <div className="tw-flex tw-gap-2">
          {!!feedbackSubmitted && (
            <Button
              size="xxs"
              variant="secondary"
              disabled={csvLoading || isCancelled}
              onClick={() => triggerCsvReportDownload()}
            >
              {csvLoading ? (
                <LoaderCircleIcon className="tw-animate-spin" />
              ) : (
                <ArrowDownToLine size={16} className="tw-mr-1" />
              )}
              <span>CSV</span>
            </Button>
          )}

          {feedbackSchedule?.sendAt && dayjs.utc(feedbackSchedule.sendAt).isBefore(dayjs.utc()) && (
            <Button
              size="xxs"
              variant="secondary"
              onClick={() => resendFeedbackFormModal.open()}
              disabled={isThirtyDaysAfterEventEnds || isCancelled}
            >
              <SendHorizontal size={16} className="tw-mr-1" />
              Resend
            </Button>
          )}
        </div>
      </div>
      {!feedbackSubmitted ? (
        <EmptyFeedbackResponses className={cn(isCancelled && 'tw-opacity-40')} />
      ) : (
        <QuestionResponses
          questions={postQuestions?.filter((q) => !q.isDeleted) || []}
          responsesTotal={feedbackSubmitted}
        />
      )}
    </div>
  );
};
